import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useBlocker } from "react-router-dom";
import { ArrowLeft, Save, GraduationCap, Copy, Trash2, CloudOff, CheckCircle2, Loader2, CircleAlert, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHistory } from "@/lib/useHistory";
import { usePlayback } from "@/lib/usePlayback";
import { contentOf, newRoute, addPoint, movePoint, deleteRoute, addBallEvent, updateQuestion, movePlayerWithRoutes, insertPoint, applyPreset, addPlayer, removePlayer } from "@/lib/playOps";
import { playerRoutes, snapTime, totalDuration, playerById } from "@/lib/playEngine";
import { presetPoints } from "@/lib/routePresets";
import { fromLibrary } from "@/lib/routeLibrary";
import { RouteTools } from "@/components/editor/RouteTools";
import { PlayerControls, RemoveDialog } from "@/components/editor/PlayerControls";
import { getSport } from "@/sports";
import { PlayCanvas } from "@/components/field/PlayCanvas";
import { PlaybackBar } from "@/components/field/PlaybackBar";
import { Legend } from "@/components/field/Legend";
import { ToolBar, ToolOptions } from "@/components/editor/ToolBar";
import { PlayerPanel } from "@/components/editor/PlayerPanel";
import { BallPanel, PhasesPanel } from "@/components/editor/BallPhasePanels";
import { PlayInfoPanel } from "@/components/editor/PlayInfoPanel";
import { QuizPanel } from "@/components/editor/QuizPanel";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const TABS = [["player", "Players"], ["ball", "Ball"], ["phases", "Phases"], ["info", "Play"], ["quiz", "Quiz"]];

function SaveState({ status, dirty }) {
  if (status === "saving") return <span className="inline-flex items-center gap-1 text-sm text-slate-300" data-testid="save-status"><Loader2 size={16} className="animate-spin" />Saving…</span>;
  if (status === "error") return <span className="inline-flex items-center gap-1 text-sm text-red-300" data-testid="save-status"><CloudOff size={16} />Not saved — draft kept on this device</span>;
  if (dirty) return <span className="inline-flex items-center gap-1 text-sm text-[#FACC15]" data-testid="save-status"><CircleAlert size={16} />Unsaved changes</span>;
  return <span className="inline-flex items-center gap-1 text-sm text-[#4ADE80]" data-testid="save-status"><CheckCircle2 size={16} />Saved</span>;
}

export default function PlayEditor() {
  const { playId } = useParams();
  const nav = useNavigate();
  const hist = useHistory(null);
  const play = hist.state;
  const [team, setTeam] = useState(null);
  const [savedJson, setSavedJson] = useState("");
  const savedPlayers = useRef([]);
  const [status, setStatus] = useState("idle");
  const [tool, setToolRaw] = useState("select");
  const [tab, setTab] = useState("player");
  const [sel, setSel] = useState(null);
  const [routeId, setRouteId] = useState(null);
  const [routeType, setRouteType] = useState("run");
  const [ballType, setBallType] = useState("snap");
  const [ballFrom, setBallFrom] = useState(null);
  const [quizFor, setQuizFor] = useState(null);
  const [draft, setDraft] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [locks, setLocks] = useState({ offense: false, defense: false });
  const [selPoint, setSelPoint] = useState(null);
  const [pending, setPending] = useState(null);
  const drag = useRef(null);
  const draftKey = `tpc_draft_${playId}`;
  const pb = usePlayback(play ? totalDuration(play) : 1);

  const dirty = useMemo(() => !!play && JSON.stringify(contentOf(play)) !== savedJson, [play, savedJson]);
  const { set: hSet, checkpoint: hCheck, endChange: hEnd } = hist;
  const edit = useMemo(() => ({ set: hSet, live: (fn) => hSet(fn, false), begin: hCheck, end: hEnd }), [hSet, hCheck, hEnd]);

  const acceptServer = useCallback((data) => {
    setSavedJson(JSON.stringify(contentOf(data)));
    savedPlayers.current = data.players;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/plays/${playId}`);
        const t = await api.get(`/teams/${data.team_id}`);
        setTeam(t.data);
        hist.reset(data);
        acceptServer(data);
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          if (raw !== JSON.stringify(contentOf(data))) setDraft(JSON.parse(raw));
          else localStorage.removeItem(draftKey);
        }
      } catch (e) { toast.error(errMsg(e)); nav("/teams"); }
    })();
  }, [playId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (play && dirty) localStorage.setItem(draftKey, JSON.stringify(contentOf(play))); }, [play, dirty, draftKey]);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);

  const save = useCallback(async () => {
    if (!hist.state) return;
    setStatus("saving");
    try {
      const { data } = await api.put(`/plays/${playId}`, contentOf(hist.state));
      hist.set((p) => ({ ...p, ...contentOf(data), updated_at: data.updated_at }), false);
      acceptServer(data);
      localStorage.removeItem(draftKey);
      setStatus("idle");
      toast.success("Play saved");
    } catch (e) { setStatus("error"); toast.error(errMsg(e)); }
  }, [hist, playId, draftKey, acceptServer]);

  useEffect(() => {
    const h = (e) => {
      if (!(e.ctrlKey || e.metaKey) || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "s") { e.preventDefault(); save(); } else if (k === "z" && e.shiftKey) { e.preventDefault(); hist.redo(); } else if (k === "z") { e.preventDefault(); hist.undo(); } else if (k === "y") { e.preventDefault(); hist.redo(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [save, hist]);

  const setTool = (t) => {
    setToolRaw(t); setBallFrom(null); pb.seek(0); setSelPoint(null);
    if (t !== "quiz") setQuizFor(null);
    if (t === "ball") setTab("ball");
    if (t === "route" || t === "move") setTab("player");
  };

  if (!play || !team) return <div className="min-h-screen grid place-items-center bg-[#070D18] text-slate-400" data-testid="editor-loading">Loading play…</div>;

  const sport = getSport(play.sport);
  const win = sport.viewWindow(play.los_y);
  const clamp = (pt) => sport.clampPoint(pt, win);
  const ownRoute = (rid) => rid && play.routes.find((r) => r.id === rid && r.player_id === sel);
  const sideOf = (id) => playerById(play, id)?.side;
  const lockedMsg = (id) => { const s = sideOf(id); if (s && locks[s]) { toast(`${s === "offense" ? "Offense" : "Defense"} is locked. Unlock it to edit.`); return true; } return false; };

  const onPlayerDown = (id) => {
    if (tool === "select") { setSel(id); setTab("player"); }
    else if (tool === "move") { setSel(id); if (lockedMsg(id)) return; hist.checkpoint(); drag.current = { type: "player", id }; }
    else if (tool === "route") { setSel(id); setSelPoint(null); const rs = playerRoutes(play, id); setRouteId(rs.length ? rs[rs.length - 1].id : null); }
    else if (tool === "ball") {
      if (!ballFrom) setBallFrom(id);
      else if (id !== ballFrom) { hist.set((p) => addBallEvent(p, ballType, ballFrom, id)); setBallFrom(null); toast.success("Ball action added. Adjust timing in the Ball tab."); }
    }
  };
  const onPointDown = (rid, idx) => {
    if (lockedMsg(play.routes.find((r) => r.id === rid)?.player_id)) return;
    hist.checkpoint(); setRouteId(rid); setSelPoint({ rid, idx }); drag.current = { type: "point", rid, idx };
  };
  const onInsert = (rid, idx) => {
    if (lockedMsg(sel)) return;
    hist.set((pl) => insertPoint(pl, rid, idx)); setSelPoint({ rid, idx });
  };
  const onFieldDown = (pt) => {
    const p = clamp(pt);
    if (tool === "select") setSel(null);
    else if (tool === "route") {
      if (!sel) { toast("Tap a player first"); return; }
      if (lockedMsg(sel)) return;
      setSelPoint(null);
      if (ownRoute(routeId)) hist.set((pl) => addPoint(pl, routeId, p));
      else hist.set((pl) => { const [next, rid] = newRoute(pl, sel, routeType, p, snapTime(pl)); setTimeout(() => setRouteId(rid)); return next; });
    } else if (tool === "quiz" && quizFor) {
      hist.set((pl) => updateQuestion(pl, quizFor, { target: { ...p, r: pl.quiz.find((q) => q.id === quizFor)?.target?.r || 3 } }));
    }
  };
  const onMove = (pt) => {
    const d = drag.current;
    if (!d) return;
    const p = clamp(pt);
    if (d.type === "player") hist.set((pl) => movePlayerWithRoutes(pl, d.id, p), false);
    else hist.set((pl) => movePoint(pl, d.rid, d.idx, p), false);
  };
  const onUp = () => { if (drag.current) { drag.current = null; hist.endChange(); } };

  const addSide = (side) => {
    const n = play.players.filter((x) => x.side === side).length;
    if (n >= team.player_count) toast.warning(`That's more than your usual ${team.player_count} per side. Fine for teaching!`);
    const x = Math.min(50, 8 + ((n * 5) % 40)), y = side === "offense" ? play.los_y + 3 : play.los_y - 5;
    hist.set((pl) => { const [next, id] = addPlayer(pl, side, side === "offense" ? "WR" : "DB", x, y); setTimeout(() => { setSel(id); setTab("player"); }); return next; });
  };
  const requestRemove = (id) => {
    if (!id || lockedMsg(id)) return;
    const used = play.ball.carrier === id || play.ball.events.some((e) => e.from_id === id || e.to_id === id);
    if (used) setPending(id); else doRemove(id, null);
  };
  const doRemove = (id, to) => { hist.set((pl) => removePlayer(pl, id, to)); setSel(null); setRouteId(null); setSelPoint(null); setPending(null); };
  const onPreset = (key) => {
    if (lockedMsg(sel)) return;
    const pl = playerById(play, sel);
    hist.set((p) => { const [next, rid] = applyPreset(p, sel, presetPoints(p, pl, key), routeType === "block" ? "run" : routeType, routeType === "motion" ? 0 : snapTime(p)); setTimeout(() => setRouteId(rid)); return next; });
    setSelPoint(null);
  };
  const applyLibrary = (item) => {
    if (lockedMsg(sel)) return;
    hist.set((p) => ({ ...p, routes: [...p.routes.filter((r) => r.player_id !== sel), ...fromLibrary(p, playerById(p, sel), item.segments)] }));
    setRouteId(null); setSelPoint(null);
    toast.success(item.name + " added to " + (playerById(play, sel)?.label || "player") + ". Undo to go back.");
  };
  const finishRoute = () => { setRouteId(null); setSelPoint(null); setTool("select"); setTab("player"); toast.success("Route finished. Press Preview to watch it."); };

  const restore = () => hist.set((p) => ({ ...p, players: p.players.map((pl) => { const s = savedPlayers.current.find((x) => x.id === pl.id); return s ? { ...pl, x: s.x, y: s.y } : pl; }) }));
  const duplicate = async () => {
    try { const { data } = await api.post(`/plays/${playId}/duplicate`); toast.success("Copy created from the last saved version"); nav(`/play/${data.id}/edit`); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/plays/${playId}`); acceptServer(play); localStorage.removeItem(draftKey); setSavedJson(JSON.stringify(contentOf(play))); toast.success("Play deleted"); setTimeout(() => nav(`/teams/${play.team_id}/playbook`)); } catch (e) { toast.error(errMsg(e)); }
  };
  const quizTarget = tool === "quiz" && quizFor ? play.quiz.find((q) => q.id === quizFor)?.target : null;
  const preview = tool === "preview";
  const editable = !preview;

  return (
    <div className="min-h-screen bg-[#070D18] text-white">
      <header className="sticky top-0 z-40 bg-[#0B1528] border-b border-[#1F3358]">
        <div className="max-w-[1500px] mx-auto px-3 py-2 flex flex-wrap items-center gap-2">
          <Link to={`/teams/${play.team_id}/playbook`} className="btn btn-ghost px-3" data-testid="editor-back-playbook"><ArrowLeft size={18} /><span className="hidden sm:inline">Playbook</span></Link>
          <Link to="/" className="btn btn-ghost px-3 hidden sm:inline-flex" data-testid="editor-back-dashboard">Home</Link>
          <div className="min-w-0 flex-1 basis-full order-last sm:basis-auto sm:order-none">
            <p className="font-display text-xl font-bold uppercase truncate leading-tight" data-testid="editor-play-name">{play.name}</p>
            <SaveState status={status} dirty={dirty} />
          </div>
          <Link to={`/play/${playId}/learn`} className="btn btn-ghost px-3" data-testid="editor-learn-link"><GraduationCap size={18} /><span className="hidden md:inline">Learn</span></Link>
          <Link to={`/teams/${play.team_id}/print?plays=${playId}`} className="btn btn-ghost px-3" aria-label="Print play" data-testid="editor-print-link"><Printer size={18} /></Link>
          <button className="btn btn-ghost px-3" onClick={duplicate} aria-label="Duplicate play" data-testid="editor-duplicate-btn"><Copy size={18} /></button>
          {team.can_manage && <button className="btn btn-danger px-3" onClick={() => setConfirmDel(true)} aria-label="Delete play" data-testid="editor-delete-btn"><Trash2 size={18} /></button>}
          <button className="btn btn-primary" onClick={save} disabled={status === "saving" || (!dirty && status !== "error")} data-testid="editor-save-btn"><Save size={18} />Save</button>
        </div>
      </header>

      {draft && (
        <div className="bg-[#FACC15] text-[#0B1528] px-4 py-2 flex flex-wrap items-center gap-3 text-sm font-bold" data-testid="draft-banner">
          Unsaved changes from this device were found.
          <button className="btn bg-[#0B1528] text-white min-h-[36px]" onClick={() => { hist.set((p) => ({ ...p, ...draft })); setDraft(null); }} data-testid="draft-restore-btn">Restore draft</button>
          <button className="btn border border-[#0B1528] min-h-[36px]" onClick={() => { localStorage.removeItem(draftKey); setDraft(null); }} data-testid="draft-discard-btn">Discard</button>
        </div>
      )}
      {play.description && play.description.startsWith("EXAMPLE") && <p className="max-w-[1500px] mx-auto px-3 pt-3 text-xs text-[#FACC15]" data-testid="example-note">{play.description}</p>}

      <div className="max-w-[1500px] mx-auto p-3 grid lg:grid-cols-[1fr_400px] gap-4">
        <section className="flex flex-col gap-3 min-w-0">
          <div className="order-4 lg:order-none"><PlayerControls play={play} team={team} sel={sel} locks={locks} setLocks={setLocks} onAdd={addSide} onRequestRemove={requestRemove} /></div>
          <div className="order-1 lg:order-none"><ToolBar tool={tool} setTool={setTool} hist={hist} /></div>
          <div className="card p-3 order-3 lg:order-none">
            {preview ? <PlaybackBar play={play} pb={pb} /> : (
              <ToolOptions tool={tool} play={play} ballType={ballType} setBallType={setBallType} ballFrom={ballFrom} setBallFrom={setBallFrom} onRestore={restore}
                onQuizDone={() => { setTool("select"); setTab("quiz"); }}
                routeTools={<RouteTools play={play} player={sel && playerById(play, sel)} route={ownRoute(routeId) || null} routeType={routeType}
                  setRouteType={(t) => { setRouteType(t); if (ownRoute(routeId)) hist.set((p) => ({ ...p, routes: p.routes.map((r) => (r.id === routeId ? { ...r, type: t } : r)) })); }}
                  selPoint={selPoint} setSelPoint={setSelPoint} edit={edit} onPreset={onPreset} onApplyLibrary={applyLibrary} onFinish={finishRoute}
                  onNewSegment={() => { setRouteId(null); setSelPoint(null); }} onDeleteRoute={() => { hist.set((p) => deleteRoute(p, routeId)); setRouteId(null); setSelPoint(null); }} />} />
            )}
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#1F3358] order-2 lg:order-none">
            <PlayCanvas play={play} team={team} t={preview ? pb.t : 0} selectedId={editable ? sel : null} pickedId={ballFrom}
              editRouteFor={tool === "route" && !locks[sideOf(sel)] ? sel : null} activeRouteId={tool === "route" ? routeId : null} selPoint={selPoint} onInsert={tool === "route" ? onInsert : undefined} quizTarget={quizTarget}
              onPlayerDown={editable && tool !== "quiz" ? onPlayerDown : undefined} onPointDown={tool === "route" ? onPointDown : undefined}
              onFieldDown={editable ? onFieldDown : undefined} onMove={editable ? onMove : undefined} onUp={editable ? onUp : undefined} />
          </div>
          <Legend className="order-5 lg:order-none" />
          <p className="text-xs text-slate-500 order-6 lg:order-none">Playback is a teaching diagram of your plan. It doesn't predict how real defenders react.</p>
        </section>

        <aside className="card p-4 h-fit lg:sticky lg:top-[76px] lg:max-h-[calc(100vh-90px)] lg:overflow-y-auto" data-testid="editor-side-panel">
          <div className="flex flex-wrap gap-1 mb-4" role="tablist">
            {TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={`chip ${tab === k ? "chip-on" : ""}`} onClick={() => setTab(k)} data-testid={`editor-tab-${k}`}>{l}</button>)}
          </div>
          {tab === "player" && <PlayerPanel play={play} team={team} edit={edit} sel={sel} setSel={setSel} routeId={routeId} setRouteId={setRouteId} setTool={setTool} onAdd={addSide} onRemove={requestRemove} locked={!!(sel && locks[sideOf(sel)])} locks={locks} />}
          {tab === "ball" && <BallPanel play={play} edit={edit} setTool={setTool} />}
          {tab === "phases" && <PhasesPanel play={play} edit={edit} />}
          {tab === "info" && <PlayInfoPanel key={play.id} play={play} team={team} edit={edit} />}
          {tab === "quiz" && <QuizPanel play={play} edit={edit} quizFor={quizFor} startTarget={(id) => { setQuizFor(id); setToolRaw("quiz"); }} />}
        </aside>
      </div>

      <RemoveDialog key={pending || "none"} play={play} pending={pending} onClose={() => setPending(null)} onRemove={doRemove} />
      <ConfirmDialog open={blocker.state === "blocked"} onOpenChange={(o) => !o && blocker.reset?.()} title="Leave without saving?" body="You have unsaved changes. A draft stays on this device, but it isn't saved to your playbook yet." confirmLabel="Leave anyway" onConfirm={() => blocker.proceed?.()} testId="confirm-leave" />
      <ConfirmDialog open={confirmDel} onOpenChange={setConfirmDel} title="Delete this play?" body={`"${play.name}" will be removed from the playbook for good.`} confirmLabel="Delete play" onConfirm={remove} testId="confirm-delete-play" />
    </div>
  );
}
